/**
 * GA4 API Response Formatters (Safe ES5/ES6 syntax for Babel compatibility)
 */

function getRows(report) {
  return report && report.rows && Array.isArray(report.rows) ? report.rows : []
}

function getMetricValue(row, index, defaultValue = '0') {
  if (
    row &&
    row.metricValues &&
    row.metricValues[index] &&
    row.metricValues[index].value !== undefined
  ) {
    return row.metricValues[index].value
  }
  return defaultValue
}

function getDimensionValue(row, index, defaultValue = '') {
  if (
    row &&
    row.dimensionValues &&
    row.dimensionValues[index] &&
    row.dimensionValues[index].value !== undefined
  ) {
    return row.dimensionValues[index].value
  }
  return defaultValue
}

/**
 * Formats Realtime GA4 Report into Analytics Payload
 */
export function formatRealtimeData(
  kpisReport,
  customEventsReport,
  trendReport,
  countryReport,
  pagesReport,
  contentGrowth
) {
  const kpiRows = getRows(kpisReport)
  const firstKpiRow = kpiRows[0] || null

  const activeToday = parseInt(getMetricValue(firstKpiRow, 0, '0'), 10)
  const totalPageViews = parseInt(getMetricValue(firstKpiRow, 1, '0'), 10)
  const totalVisits = parseInt(getMetricValue(firstKpiRow, 2, '0'), 10)

  let totalSearches = 0
  let totalQrScans = 0
  const customEventRows = getRows(customEventsReport)
  customEventRows.forEach((row) => {
    const eName = getDimensionValue(row, 0, '')
    const eCount = parseInt(getMetricValue(row, 0, '0'), 10)
    if (eName === 'view_search_results' || eName === 'search') {
      totalSearches += eCount
    } else if (eName === 'qr_scan') {
      totalQrScans += eCount
    }
  })

  const trendRows = getRows(trendReport)
  const trafficTrend = trendRows.map((row) => {
    const minutesAgo = getDimensionValue(row, 0, '0')
    return {
      date: `${minutesAgo.padStart(2, '0')}p trước`,
      visits: parseInt(getMetricValue(row, 1, '0'), 10),
      users: parseInt(getMetricValue(row, 0, '0'), 10)
    }
  })

  let cumulative = 0
  const userGrowth = trafficTrend.map((t) => {
    cumulative += t.users
    return {
      date: t.date,
      totalUsers: cumulative
    }
  })

  const rawCountries = getRows(countryReport)
  const countryTotalVisits = rawCountries.reduce(
    (acc, row) => acc + parseInt(getMetricValue(row, 0, '0'), 10),
    0
  )
  const countryShare = rawCountries.map((row) => {
    const count = parseInt(getMetricValue(row, 0, '0'), 10)
    return {
      country: getDimensionValue(row, 0, 'Khác'),
      count,
      percentage:
        countryTotalVisits > 0
          ? parseFloat(((count / countryTotalVisits) * 100).toFixed(1))
          : 0
    }
  })

  const rawPages = getRows(pagesReport)
  const pageTotalViews = rawPages.reduce(
    (acc, row) => acc + parseInt(getMetricValue(row, 0, '0'), 10),
    0
  )
  const pageStats = rawPages.map((row) => {
    const views = parseInt(getMetricValue(row, 0, '0'), 10)
    const pagePath = getDimensionValue(row, 0, '/')
    return {
      path: pagePath,
      title: pagePath,
      views,
      percentage:
        pageTotalViews > 0
          ? parseFloat(((views / pageTotalViews) * 100).toFixed(1))
          : 0
    }
  })

  return {
    errorMsg: null,
    rangeType: 'realtime',
    rangeLabel: '30 phút qua',
    kpis: {
      totalVisits,
      activeToday,
      activeThisMonth: activeToday,
      newVisitors: 0,
      returningVisitors: 0,
      avgTimeSeconds: 0,
      totalPageViews,
      bounceRate: 0,
      totalSearches,
      totalQrScans
    },
    charts: {
      trafficTrend,
      userGrowth,
      contentGrowth,
      countryShare,
      pageStats,
      qrStats: []
    }
  }
}

/**
 * Formats Standard Historical GA4 Report into Analytics Payload
 */
export function formatStandardData(
  kpisReport,
  customEventsReport,
  trendReport,
  countryReport,
  pagesReport,
  qrReport,
  rangeType,
  rangeLabel,
  contentGrowth
) {
  const kpiRows = getRows(kpisReport)
  const firstKpiRow = kpiRows[0] || null

  const totalVisits = parseInt(getMetricValue(firstKpiRow, 0, '0'), 10)
  const activeToday = parseInt(getMetricValue(firstKpiRow, 1, '0'), 10)
  const newVisitors = parseInt(getMetricValue(firstKpiRow, 2, '0'), 10)
  const totalPageViews = parseInt(getMetricValue(firstKpiRow, 3, '0'), 10)
  const bounceRate = parseFloat(getMetricValue(firstKpiRow, 4, '0')) * 100
  const avgTimeSeconds = Math.round(parseFloat(getMetricValue(firstKpiRow, 5, '0')))

  const activeThisMonth = activeToday
  const returningVisitors = Math.max(0, activeThisMonth - newVisitors)

  let totalSearches = 0
  let totalQrScans = 0
  const customEventRows = getRows(customEventsReport)
  customEventRows.forEach((row) => {
    const eName = getDimensionValue(row, 0, '')
    const eCount = parseInt(getMetricValue(row, 0, '0'), 10)
    if (eName === 'view_search_results' || eName === 'search') {
      totalSearches += eCount
    } else if (eName === 'qr_scan') {
      totalQrScans += eCount
    }
  })

  const trendRows = getRows(trendReport)
  const trafficTrend = trendRows.map((row) => {
    const rawDate = getDimensionValue(row, 0, '')
    const day = rawDate.length >= 8 ? rawDate.substring(6, 8) : rawDate
    const month = rawDate.length >= 8 ? rawDate.substring(4, 6) : ''
    return {
      date: month ? `${day}/${month}` : rawDate,
      visits: parseInt(getMetricValue(row, 0, '0'), 10),
      users: parseInt(getMetricValue(row, 1, '0'), 10)
    }
  })

  let cumulative = Math.max(0, activeThisMonth - 100)
  const userGrowth = trafficTrend.map((t) => {
    cumulative += Math.round(t.users * 0.15)
    return {
      date: t.date,
      totalUsers: cumulative
    }
  })

  const rawCountries = getRows(countryReport)
  const countryTotalVisits = rawCountries.reduce(
    (acc, row) => acc + parseInt(getMetricValue(row, 0, '0'), 10),
    0
  )
  const countryShare = rawCountries.map((row) => {
    const count = parseInt(getMetricValue(row, 0, '0'), 10)
    return {
      country: getDimensionValue(row, 0, 'Khác'),
      count,
      percentage:
        countryTotalVisits > 0
          ? parseFloat(((count / countryTotalVisits) * 100).toFixed(1))
          : 0
    }
  })

  const rawPages = getRows(pagesReport)
  const pageTotalViews = rawPages.reduce(
    (acc, row) => acc + parseInt(getMetricValue(row, 0, '0'), 10),
    0
  )
  const pageStats = rawPages.map((row) => {
    const views = parseInt(getMetricValue(row, 0, '0'), 10)
    const pagePath = getDimensionValue(row, 0, '/')
    return {
      path: pagePath,
      title: pagePath,
      views,
      percentage:
        pageTotalViews > 0
          ? parseFloat(((views / pageTotalViews) * 100).toFixed(1))
          : 0
    }
  })

  const rawQr = getRows(qrReport)
  const qrTotalScans = rawQr.reduce(
    (acc, row) => acc + parseInt(getMetricValue(row, 0, '0'), 10),
    0
  )
  const qrStats = rawQr.map((row) => {
    const scans = parseInt(getMetricValue(row, 0, '0'), 10)
    const pagePath = getDimensionValue(row, 0, '/')
    return {
      path: pagePath,
      title: pagePath,
      scans,
      percentage:
        qrTotalScans > 0
          ? parseFloat(((scans / qrTotalScans) * 100).toFixed(1))
          : 0
    }
  })

  return {
    errorMsg: null,
    rangeType,
    rangeLabel,
    kpis: {
      totalVisits,
      activeToday,
      activeThisMonth,
      newVisitors,
      returningVisitors,
      avgTimeSeconds,
      totalPageViews,
      bounceRate: parseFloat(bounceRate.toFixed(1)),
      totalSearches,
      totalQrScans
    },
    charts: {
      trafficTrend,
      userGrowth,
      contentGrowth,
      countryShare,
      pageStats,
      qrStats
    }
  }
}
